import { getAppConfigSingleton } from "@expert/app-config";
import { useNotification } from "@expert/common-ui";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useVerifiedCustomer } from "@expert/sales-tools";
import { useCallback, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { useCallCategoryStore } from "@/call-controls/hooks/callCategoryStore";
import { useTaskMdn } from "@/customer-panel/hooks/useTaskMdn";
import { usePartner, useSession } from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { getHashedValue, removeCountryCode } from "@/shared-utils";

import type { SummarySource } from "../api/selectSummarySource";

import { getServedSessionSummary } from "../api/getSessionSummary";
import { useSimplrSummaryRouting } from "../api/useSimplrSummaryRouting";
import { sanitizeRemarkText } from "../sanitizeRemarkText";
import { useSessionSummaryStore } from "../store/useSessionSummaryStore";
import { selectSummaryDelivery } from "../summaryDelivery";
import { evaluateSummaryDispatch } from "../summaryDispatchGate";
import { getPartnerTool } from "../utils";
import { useSessionSummaryDispatchActions } from "./hooks";
import { defaultAdditionalSummaryTimes } from "./utils";

/**
 * Hook for session summary logic to separate from UI components
 */
export const useSummary = () => {
    const logger = useLogger({ module: "sessionSummary" });
    const appConfig = getAppConfigSingleton();
    const activeSession = useSession();
    const partner = usePartner();
    const notification = useNotification();

    const { summaryConfig, isSimplrSummaryFlagsLoading } = useSimplrSummaryRouting();

    const [summaryGenerated, setSummaryGenerated] = useState(false);

    const summary = useSessionSummaryStore((state) => state.summary);
    const submitted = useSessionSummaryStore((state) => state.submitted);
    const customer = useVerifiedCustomer(activeSession.id);
    const { mdn: verifiedMdn, accountNumber } = customer ?? {};

    const {
        allowedAccountNumberLengths,
        blocklistedNumbers,
        blocklistedPrefixes,
        blocklistedSubscriberNumbers,
        requiresManualCopy,
        gaiaSummaryModel,
        sessionSummaryTimes = defaultAdditionalSummaryTimes,
    } = appConfig.getPartnersConfig(partner);

    // The `?? false` matters: this used to fall back to an object, which is truthy, so a partner
    // without `allowedAccountNumberLengths` configured reported a valid account number.
    const validAccountNumber =
        accountNumber !== undefined && (allowedAccountNumberLengths?.includes(accountNumber.length) ?? false);

    const shouldSendSummary = !submitted && summary;

    const {
        features: { includesSendToPartnerMeta, sendSummaryFromBackend, useMdnForAccountNo },
    } = useFeatures<WorkspaceFeatures>();
    const { dispatchSessionSubmit, dispatchSessionSummaryStats } = useSessionSummaryDispatchActions();
    const { resolutionStatus, category, subcategory } = useSessionSummaryStore(
        useShallow((state) => ({
            resolutionStatus: state.resolutionStatus,
            category: state.category,
            subcategory: state.subcategory,
        })),
    );
    const taskMdn = useTaskMdn({ blocklistedNumbers, blocklistedPrefixes });
    const mdn = taskMdn || verifiedMdn || "";
    const extraSessionSummaryPayloadProps = appConfig.getPartnersConfig(partner).extraSessionSummaryPayloadProps;

    /** Dispatch analytics with post-call summary to EIP */
    const sendSessionSummaryToEIP = useCallback(
        async (summaryText: string, agentCallSid: string, servedBy: SummarySource) => {
            // Guard against duplicate dispatch (e.g., component remount re-firing the
            // SessionSummary auto-send useEffect). The Zustand `submitted` flag survives
            // remounts, so the second invocation bails before reaching the dispatcher.
            if (useSessionSummaryStore.getState().submitted) {
                return;
            }

            const mdnFormatted = removeCountryCode(mdn);
            const mdnHashed = await getHashedValue(mdnFormatted);

            const remarkText = sanitizeRemarkText(summaryText);
            const sessionSummaryPayload = {
                accountNo: useMdnForAccountNo ? mdnFormatted : accountNumber,
                remarkText,
                resolutionStatus,
                ...extraSessionSummaryPayloadProps,
                ...(category ? { category } : {}),
                ...(subcategory ? { subcategory } : {}),
            };

            // Only forward to the partner tool when the value we would send is actually usable.
            // This flow is not live for att today, but it dispatches the same `accountNo`, so it
            // gets the same gate rather than being left to regress once it is enabled.
            const { canSendToPartner, identifierIsUsable, reason } = evaluateSummaryDispatch({
                accountNumber,
                allowedAccountNumberLengths,
                blocklistedNumbers,
                blocklistedPrefixes,
                blocklistedSubscriberNumbers,
                remarkText,
                subscriberNumber: mdnFormatted,
                useMdnForAccountNo: !!useMdnForAccountNo,
            });
            if (!identifierIsUsable) {
                logger.warn(
                    { partner, reason, subscriberNumberDigits: mdnFormatted.replace(/\D/g, "").length },
                    canSendToPartner
                        ? "Session summary identifier failed validation"
                        : "Session summary not forwarded to partner tool",
                );
            }

            // Set submitted before dispatch so concurrent re-fires see the flag and bail.
            useSessionSummaryStore.getState().setSubmitted(true);
            try {
                const delivery = selectSummaryDelivery({ sendSummaryFromBackend: !!sendSummaryFromBackend, servedBy });
                logger.info({ partner, agentCallSid, servedBy, delivery }, "Session summary delivery selected");
                if (delivery === "frontend") {
                    dispatchSessionSubmit({
                        agentCallSid,
                        mdn: mdnFormatted,
                        mdnHashed,
                        sendToPartner: !!includesSendToPartnerMeta && canSendToPartner,
                        sessionSummaryPayload,
                    });
                }
                notification.success("Summary sent");
            } catch (error) {
                // Roll back the optimistic submitted flag so a manual retry isn't blocked by the dedupe guard.
                useSessionSummaryStore.getState().setSubmitted(false);
                notification.error(
                    `Failed to send session summary! Copy notes manually to ${getPartnerTool(partner)}.`,
                );
                logger.warn({ error }, "Failed to send session summary!");
            }

            // dispatch session summary stats without PII so we have an analytic in OpenSearch
            dispatchSessionSummaryStats({
                agentCallSid,
                autogeneratedSummary: summaryText,
                expirationTimestamp: 0,
                gaiaSummaryModel,
                summaryWasCopiedToClipboard: false,
                summaryWasSubmittedViaPartnerTool: undefined,
                timeRanOut: false,
                update: summaryText,
                gaiaCallCategory: useCallCategoryStore.getState().callCategory,
            });
        },
        [
            accountNumber,
            allowedAccountNumberLengths,
            blocklistedNumbers,
            blocklistedPrefixes,
            blocklistedSubscriberNumbers,
            category,
            dispatchSessionSubmit,
            dispatchSessionSummaryStats,
            extraSessionSummaryPayloadProps,
            gaiaSummaryModel,
            includesSendToPartnerMeta,
            logger,
            mdn,
            notification,
            partner,
            resolutionStatus,
            sendSummaryFromBackend,
            subcategory,
            useMdnForAccountNo,
        ],
    );

    const fetchAndDispatchSessionSummary = useCallback(
        async (setSummaryLoaded: (arg0: boolean) => void, agentCallSid?: string) => {
            const logIdentities = { agentCallSid, sessionId: activeSession.id, partner };
            logger.info(logIdentities, "Session summary fetch check");

            const { summary: generatedSummary, servedBy } = await getServedSessionSummary(
                logger,
                { callSid: agentCallSid, sessionId: activeSession.id, partner },
                gaiaSummaryModel,
                summaryConfig,
            );

            const summarySourceLabel = summaryConfig.source === "simplr" ? "Simplr" : "GAIA";
            if (!generatedSummary) {
                logger.warn(logIdentities, `Received empty summary from ${summarySourceLabel}.`);
            } else {
                logger.info(`Received non-empty summary from ${summarySourceLabel}.`);
                useSessionSummaryStore.getState().setSessionSummary({ value: generatedSummary, autoGenerated: true });
                setSummaryGenerated(!!generatedSummary);
                void sendSessionSummaryToEIP(generatedSummary, agentCallSid ?? "", servedBy);
                logger.info(logIdentities, `Sent ${summarySourceLabel} session summary to analytics service.`);
            }
            setSummaryLoaded(true);
        },
        [activeSession.id, logger, partner, gaiaSummaryModel, sendSessionSummaryToEIP, summaryConfig],
    );

    const onDismiss = useCallback(
        async (closeModal: () => void, _agentCallSid?: string, { checkSummary = true } = {}) => {
            logger.trace("Session summary dismissed");
            closeModal();
            if (checkSummary && shouldSendSummary) {
                notification.success("Session summary copied to clipboard");
                try {
                    await navigator.clipboard.writeText(summary);
                } catch (error) {
                    logger.warn({ error }, "Session summary not copied to clipboard");
                }
            }
        },
        [logger, shouldSendSummary, summary, notification],
    );

    const getExtraSummaryTime = useCallback(
        (canAutosend: boolean, hasSummaryOnly: boolean) => {
            if (canAutosend) {
                return sessionSummaryTimes.autosend;
            } else if (hasSummaryOnly) {
                return sessionSummaryTimes.manualCopy;
            }
            return sessionSummaryTimes.maximum;
        },
        [sessionSummaryTimes],
    );

    return {
        summaryGenerated,
        validAccountNumber,
        requiresManualCopy,
        sessionSummaryTimes,
        summary,
        fetchAndDispatchSessionSummary,
        isSimplrSummaryFlagsLoading,
        onDismiss,
        getExtraSummaryTime,
    };
};
